import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Controls whose activation represents a genuine, user-initiated add to cart.
 * @type {string}
 */
const ADD_TO_CART_SELECTOR = [
  '[name="add"]',
  '.add-to-cart-button',
  'add-to-cart-component',
  'product-form-component',
].join(', ');

/**
 * How long a recorded user intent stays valid, in milliseconds. Generous enough to
 * cover a slow add-to-cart request, short enough that it cannot leak into a later
 * app-initiated cart change.
 * @type {number}
 */
const USER_INTENT_TIMEOUT = 15000;

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /**
   * Scroll position captured when the drawer opens (read from body.style.top
   * which dialog.js sets as part of its scroll-lock). Stored here so that
   * #handleHistoryClose always has the *original* position — even if
   * dialog.close() or focus-restoration has already scrolled the page away.
   * @type {number}
   */
  #scrollBeforeOpen = 0;

  /**
   * Whether a genuine, user-initiated add to cart is currently in flight.
   * @type {boolean}
   */
  #hasUserIntent = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #userIntentTimer = null;

  /** @type {AbortController | null} */
  #intentAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    // Record real user activation of an add-to-cart control. Captured on document so
    // it runs before the form handler that performs the add.
    this.#intentAbortController = new AbortController();
    const { signal } = this.#intentAbortController;
    document.addEventListener('click', this.#recordUserIntent, { capture: true, signal });
    document.addEventListener('submit', this.#recordUserIntent, { capture: true, signal });

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#intentAbortController?.abort();
    this.#clearUserIntent();
  }

  /**
   * Flags an add to cart as user-initiated when a real interaction hits an add-to-cart control.
   * @param {Event} event - The originating click or submit event.
   */
  #recordUserIntent = (event) => {
    // isTrusted excludes programmatic clicks/submits dispatched by apps or scripts.
    if (!event.isTrusted) return;

    const target = event.target;
    if (!(target instanceof Element) || !target.closest(ADD_TO_CART_SELECTOR)) return;

    this.#hasUserIntent = true;
    if (this.#userIntentTimer) clearTimeout(this.#userIntentTimer);
    this.#userIntentTimer = setTimeout(() => this.#clearUserIntent(), USER_INTENT_TIMEOUT);
  };

  /**
   * Clears any recorded user intent.
   */
  #clearUserIntent() {
    this.#hasUserIntent = false;
    if (this.#userIntentTimer) clearTimeout(this.#userIntentTimer);
    this.#userIntentTimer = null;
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    // dialog.js sets body { position:fixed; top: -<scrollY>px } synchronously
    // inside a rAF, and DialogOpenEvent fires inside that same rAF callback,
    // so body.style.top is already set here. Parse it to get the true scroll.
    const bodyTop = document.body.style.top;
    this.#scrollBeforeOpen = bodyTop ? Math.abs(parseInt(bodyTop, 10)) : window.scrollY;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      const restoreScrollY = this.#scrollBeforeOpen;

      // Prevent the browser from overriding our scroll on history.back()
      history.scrollRestoration = 'manual';
      history.back();

      // Restore scroll on popstate, then again one frame later to win any
      // browser-native focus-scroll that fires asynchronously on mobile.
      window.addEventListener('popstate', () => {
        window.scrollTo({ top: restoreScrollY, behavior: 'instant' });
        document.documentElement.scrollTop = restoreScrollY;
        requestAnimationFrame(() => {
          window.scrollTo({ top: restoreScrollY, behavior: 'instant' });
          document.documentElement.scrollTop = restoreScrollY;
          history.scrollRestoration = 'auto';
        });
      }, { once: true });
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    if (event.detail.data?.didError) return;

    // CartAddEvent shares the public 'cart:update' event name with CartUpdateEvent, and
    // third-party apps dispatch 'cart:update' too — automatic gift/free-product apps fire
    // it on page load when they apply their cart rules. Opening on the event alone made
    // the drawer appear on refresh and navigation, so require a real user action on an
    // add-to-cart control. The cart icon opens the drawer separately via /open.
    if (this.hasAttribute('auto-open') && this.#hasUserIntent) {
      this.#clearUserIntent();
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
